import { IonChip, IonLabel } from '@ionic/react';
import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { BeeChatViewModel, IBeeChatTrackingFrom, HELPER_BEE_OPENED_STORAGE_KEY } from './BeeChatViewModel';
import { useSubscription } from '@meraki-internal/state';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { useIfLoggedIn } from '../utils/useIfLoggedIn';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CommentQuestionIcon } from '../theme/Icons';


const useStyles = createUseStyles({
    '@keyframes beaSonar': {
        '0%': {
            transform: 'translate(-50%, -50%) scale(1)',
            borderColor: 'rgba(255, 255, 255, 0.7)'
        },
        '100%': {
            transform: 'translate(-50%, -50%) scale(1.5)',
            borderColor: 'transparent'
        }
    },
    chip: {
        height: 40,
        color: '#fff',
        paddingLeft: 6,
        borderRadius: 24,
        overflow: 'visible',
        textAlign: 'center',
        border: '2px solid #fff',
        '--background': 'linear-gradient(rgba(22, 166, 217, 1), rgba(8, 63, 82, 1))',

        '& ion-label': {
            flexGrow: 1,
            fontWeight: 500
        }
    },
    iconContainer: {
        position: 'relative',
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 4
    },
    sonarRing: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '2px solid transparent',
        animation: '$beaSonar 2.5s ease-out infinite',
        pointerEvents: 'none',
        zIndex: 1,
        willChange: 'transform'
    },
    badge: {
        width: 24,
        height: 24,
        top: -8,
        right: -8,
        borderRadius: '50%',
        position: 'absolute',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ion-color-danger)',
        boxShadow: '1px 1px 10px 0 rgba(0, 0, 0, 0.4)'
    },
    minimizedBadge: {
        position: 'absolute',
        top: 1,
        right: -3,
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: 'var(--ion-color-danger)',
        border: '2px solid white',
        zIndex: 101
    }
});

export type IHelperBeeButtonProps = {
    label?: string;
    'data-id'?: string;
    coloredBorder?: boolean;
    trackingFrom: IBeeChatTrackingFrom;
};

const labels = [
    'Need caregiving help?',
    'Got a care question?',
    'Your care consultant here!',
    'Bea is here to help!',
    'Looking for answers?',
    'Chat with Bea for help!',
    'Ask Bea anything!',
    'Care questions? Just ask!',
    'Need support? Talk to Bea!',
    'Your Care Consultant is here!',
    'Get support here!',
    'Caregiving help is here!',
    'Need a Care Consultant?',
    'Help is one tap away!',
    'Ask Bea, get answers!',
    `Bea's got your back!`
];

export const HelperBeeButton: FC<IHelperBeeButtonProps> = ({
    label,
    trackingFrom,
    'data-id': dataId,
    coloredBorder
}) => {
    const classes = useStyles();
    const storageProvider = useInstance(AsyncStorageProvider);
    const ifLoggedIn = useIfLoggedIn();
    const history = useInstance(HistoryViewModel);

    const chatVM = useInstance(BeeChatViewModel);
    useSubscription(() => chatVM);

    const [showBadge, setShowBadge] = useState<boolean | undefined>(false);
    const [hasOpenedChat, setHasOpenedChat] = useState<boolean>(true);
    const [realLabel, setRealLabel] = useState<string | undefined>(label);

    useEffect(() => {
        if (!label) {
            setRealLabel(labels[Math.floor(Math.random() * labels.length)]);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useAsyncEffect(async () => {
        const value = await storageProvider.getBooleanProvider(HELPER_BEE_OPENED_STORAGE_KEY).get();
        setShowBadge(!value);
        setHasOpenedChat(Boolean(value));
    }, [chatVM.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // Trigger pings when component mounts and user hasn't opened chat
    useEffect(() => {
        if (!hasOpenedChat) {
            chatVM.triggerPings('home');
        }
    }, [hasOpenedChat, chatVM]);

    const onClickChip = () => {
        ifLoggedIn({
            delegate: async () => chatVM.open({ trackingFrom }),
            redirect: `${history.getCurrentLocation().pathname}?openChat=true`,
            message: 'Please sign in or create an account to chat with Bea.',
            trackingFrom: 'BeeLine Chat Button'
        });
    };

    const shouldShowPings = !hasOpenedChat && chatVM.showHomePings();
    const isMinimized = chatVM.isMinimized();

    return (
        <div data-id={dataId} style={{maxWidth: 260}}>
            <IonChip
                onClick={onClickChip}
                className={classes.chip}
                style={coloredBorder ? {border: '2px solid #b6e3f4'} : {}}
            >
                <div className={classes.iconContainer}>
                    {shouldShowPings && (
                        <>
                            <div className={classes.sonarRing} />
                            <div className={classes.sonarRing} style={{ animationDelay: '2.5s' }} />
                            <div className={classes.sonarRing} style={{ animationDelay: '5s' }} />
                        </>
                    )}
                    <CommentQuestionIcon color="light" customSize={18} />
                </div>
                <IonLabel style={{marginTop: -2}}>{realLabel}</IonLabel>
                {showBadge && <div data-id="helper-bee-badge" className={classes.badge}>1</div>}
                {isMinimized && <div className={classes.minimizedBadge} data-id="minimized-badge" />}
            </IonChip>
        </div>
    );
};
